import { useState, useEffect } from "react";
import { Plus, Trash, Copy, WarningCircle, ArrowBendDownRight, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const num = (v) => { const x = parseFloat(v); return isFinite(x) ? x : 0; };
const money = (x) => "₹" + Math.round(x).toLocaleString("en-IN");
const clampD = (d) => Math.max(0, Math.min(0.95, d));

// Formula-based negative (disability) factors. Each returns a deduction fraction d (0..1); F = 1 - d.
export const NEG_DEFS = {
  road_widening: {
    label: "Road widening / Master-Plan reservation",
    params: [{ k: "p", l: "Affected area %" }, { k: "lam", l: "Residual utility λ (0–0.25)" }],
    d: (p) => (num(p.p) / 100) * (1 - num(p.lam)),
  },
  ht_corridor: {
    label: "HT line corridor",
    params: [{ k: "w", l: "Corridor width W (ft)" }, { k: "l", l: "Length across L (ft)" }, { k: "a", l: "Plot area A (sq.ft)" }, { k: "delta", l: "δ (0.15–0.80)" }, { k: "s", l: "Stigma s %" }],
    d: (p) => { const a = num(p.a); const frac = a > 0 ? (num(p.w) * num(p.l)) / a : 0; return frac * num(p.delta) + num(p.s) / 100; },
  },
  low_lying: {
    label: "Low-lying / filling",
    params: [{ k: "h", l: "Depth below road h (ft)" }, { k: "c", l: "Fill cost c (₹/cu.ft)" }, { k: "t", l: "Consolidation t %" }],
    d: (p, G) => (G > 0 ? (num(p.h) * num(p.c)) / G : 0) + num(p.t) / 100,
  },
  irregular_shape: {
    label: "Irregular shape",
    params: [{ k: "u", l: "Usable rectangle %" }, { k: "beta", l: "Leftover utility β (0.3–0.5)" }],
    d: (p) => (1 - num(p.u) / 100) * (1 - num(p.beta)),
  },
  excess_depth: {
    label: "Excess depth (4-3-2-1)",
    params: [{ k: "D", l: "Actual depth D (ft)" }, { k: "Ds", l: "Standard depth Ds (ft)" }, { k: "tail", l: "Excess rate factor (0.25–0.5)" }],
    d: (p) => { const D = num(p.D), Ds = num(p.Ds), tail = num(p.tail); if (D <= Ds || D <= 0) return 0; return 1 - (Ds + tail * (D - Ds)) / D; },
  },
  deficient_frontage: {
    label: "Deficient frontage",
    params: [{ k: "f", l: "Actual frontage f (ft)" }, { k: "fs", l: "Standard frontage fs (ft)" }, { k: "k", l: "Exponent k (0.3–0.5)" }],
    d: (p) => { const f = num(p.f), fs = num(p.fs), k = num(p.k); if (fs <= 0 || f >= fs) return 0; return 1 - Math.pow(f / fs, k); },
  },
  large_extent: {
    label: "Large extent",
    params: [{ k: "A", l: "Plot area A (acres)" }],
    d: (p) => { const A = num(p.A); if (A <= 0.5) return 0; return Math.min(0.40, 0.108 * Math.log(A / 0.5)); },
  },
  encroachment: {
    label: "Encroachment / unauthorised occupation",
    params: [{ k: "pe", l: "Encroached area %" }, { k: "pi", l: "Recovery prob. π %" }, { k: "r", l: "Litigation cost r %" }],
    d: (p) => (num(p.pe) / 100) * (1 - num(p.pi) / 100) + num(p.r) / 100,
  },
  leasehold: {
    label: "Leasehold / Nazul",
    params: [{ k: "n", l: "Unexpired term n (yrs)" }, { k: "i", l: "Discount rate i %" }, { k: "pc", l: "Conversion premium ₹/sq.ft" }],
    d: (p, G) => { const n = num(p.n), i = num(p.i) / 100, pc = num(p.pc); const F = (1 - Math.pow(1 + i, -n)) - (G > 0 ? pc / G : 0); return 1 - F; },
  },
  agricultural: {
    label: "Agricultural (not diverted)",
    params: [{ k: "premium", l: "Diversion premium ₹/sq.ft" }, { k: "dev", l: "Development charges ₹/sq.ft" }, { k: "risk", l: "Approval risk %" }],
    d: (p, G) => (G > 0 ? (num(p.premium) + num(p.dev)) / G : 0) + num(p.risk) / 100,
  },
};

export const NEG_SLABS = [
  { group: "Access & approach", items: [
    { label: "Approach 4.5–6 m", lo: 8, hi: 15 }, { label: "Approach 3–4.5 m", lo: 15, hi: 25 },
    { label: "Approach <3 m / kuchcha", lo: 25, hi: 35 }, { label: "Landlocked / easement", lo: 35, hi: 50 }] },
  { group: "Physical", items: [
    { label: "Undulating / rocky", lo: 5, hi: 10 }, { label: "Nallah on / through plot", lo: 5, hi: 15 }] },
  { group: "Environmental / stigma", items: [
    { label: "Near cremation / graveyard", lo: 10, hi: 20 }, { label: "Nallah / sewage / STP / landfill", lo: 5, hi: 15 },
    { label: "Railway / highway noise (resi.)", lo: 5, hi: 10 }, { label: "Industrial pollution zone", lo: 5, hi: 15 }] },
  { group: "Regulatory", items: [
    { label: "Airport height zone (low FAR)", lo: 5, hi: 15 }, { label: "Heritage / ASI zone", lo: 10, hi: 30 }] },
  { group: "Title / legal", items: [
    { label: "Title chain gaps / mutation pending", lo: 5, hi: 10 }, { label: "Joint / undivided share", lo: 10, hi: 20 }] },
];

function itemD(it, G) {
  if (it.type === "custom") return clampD(num(it.d) / 100);
  const def = NEG_DEFS[it.type];
  return def ? clampD(def.d(it.p || {}, G)) : 0;
}
function itemLabel(it) {
  if (it.type === "custom") return it.label || "Factor";
  return (NEG_DEFS[it.type] || {}).label || it.type;
}

export function computeNegative(G, neg, baseRate = 0) {
  neg = neg || {};
  const items = neg.items || [];
  const rows = items.map((it) => { const d = itemD(it, G); return { label: itemLabel(it), d, F: 1 - d }; });
  const piF = rows.reduce((p, r) => p * r.F, 1);
  const base = baseRate > 0 ? baseRate : G;
  const fromComparables = baseRate > 0;
  const M = base * piF;
  const totalDed = base - M;
  const lnPi = piF > 0 ? Math.log(piF) : 0;
  rows.forEach((r) => {
    const lnF = r.F > 0 ? Math.log(r.F) : 0;
    r.share = lnPi !== 0 ? lnF / lnPi : 0;
    r.rupees = r.share * totalDed;
  });
  return { G, base, fromComparables, M, piF, totalDed, rows, n: items.length,
    crossCheck: piF > 0 && piF < 0.70, reasoning: piF > 0 && piF < 0.50,
    belowSafe: G > 0 && M > 0 && M < 0.9 * G };
}

export function negativeParagraph(G, neg, baseRate = 0) {
  const r = computeNegative(G, neg, baseRate);
  if (!r.base || !r.n || r.totalDed <= 0) return "";
  const baseLabel = r.fromComparables
    ? `the comparable-adopted market rate of ${money(r.base)}/sq.ft (Step 3)`
    : `the Collector Guideline rate of ${money(r.base)}/sq.ft`;
  const list = r.rows.map((x) => `${x.label} (−${(x.d * 100).toFixed(1)}%)`).join(", ");
  let s = `The subject land suffers from site-specific disabilities not reflected in ${baseLabel}: ${list}. Each disability is quantified from measurable parameters and applied multiplicatively (Annexure). The combined factor ΠF = ${r.piF.toFixed(3)} yields a Net Adopted Rate of ${money(r.M)}/sq.ft (a deduction of ${money(r.totalDed)}/sq.ft), apportioned between factors in proportion to their logarithmic weights.`;
  if (r.crossCheck) s += ` As the total deduction exceeds 30% (ΠF < 0.70), the rate has been cross-checked by the development (residual) method.`;
  if (r.reasoning) s += ` The deduction exceeds 50% (ΠF < 0.50); an explicit reasoning note is recorded.`;
  if (r.belowSafe) s += ` The value is more than 10% below the guideline rate — outside the Sec. 50C / 56(2)(x) safe harbour.`;
  return s;
}

// Residual / development method: land rate from development potential, per sq.ft of land.
export function computeResidual(res, netM) {
  res = res || {};
  const far = num(res.far);
  const effPct = (res.eff === "" || res.eff == null) ? 80 : num(res.eff);
  const saleRate = num(res.saleRate);
  const constCost = num(res.constCost);
  const profitPct = num(res.profitPct);
  const otherPct = num(res.otherPct);
  const builtPerLand = far * effPct / 100;
  const gdv = builtPerLand * saleRate;
  const construction = builtPerLand * constCost;
  const profit = gdv * profitPct / 100;
  const other = gdv * otherPct / 100;
  const residualRate = gdv - construction - profit - other;
  const divergence = netM ? (residualRate - netM) / netM : 0;
  return { far, effPct, builtPerLand, gdv, construction, profit, other, residualRate, divergence,
    corroborated: residualRate > 0 && netM > 0 && Math.abs(divergence) <= 0.15,
    ready: far > 0 && saleRate > 0 };
}

function Chip({ children, onClick, title, testid }) {
  return (
    <button type="button" onClick={onClick} title={title} data-testid={testid}
      className="text-[10px] px-2 py-0.5 rounded-full border bg-background hover:bg-accent hover:border-primary transition-colors">
      {children}
    </button>
  );
}

export default function NegativeFactors({ G, value, onChange, onAdopt, baseRate = 0 }) {
  const neg = value || {};
  const items = neg.items || [];
  const setItems = (next) => onChange({ ...neg, items: next });
  const addFormula = (type) => setItems([...items, { type, p: {} }]);
  const addSlab = (s) => setItems([...items, { type: "custom", label: s.label, d: String(Math.round((s.lo + s.hi) / 2)) }]);
  const setItem = (i, upd) => setItems(items.map((it, k) => (k === i ? { ...it, ...upd } : it)));
  const setParam = (i, key, v) => setItem(i, { p: { ...(items[i].p || {}), [key]: v } });
  const removeItem = (i) => setItems(items.filter((_, k) => k !== i));

  const r = computeNegative(G, neg, baseRate);
  const pct = (x) => (x * 100).toFixed(1) + "%";
  const para = negativeParagraph(G, neg, baseRate);

  const res = neg.residual || {};
  const rc = computeResidual(res, r.M);
  const setResidual = (upd) => onChange({ ...neg, residual: { ...res, ...upd } });

  // Factor library
  const [sets, setSets] = useState([]);
  const [libOpen, setLibOpen] = useState(false);
  const [libName, setLibName] = useState("");
  const [libLoc, setLibLoc] = useState("");
  const [selKey, setSelKey] = useState(0);
  const loadSets = () => api.get("/factor-sets").then((rp) => setSets(rp.data)).catch(() => {});
  useEffect(() => { loadSets(); }, []);
  const saveSet = async () => {
    if (!libName.trim()) { toast.error("Enter a set name"); return; }
    if (!items.length) { toast.error("Add at least one factor to save"); return; }
    try { await api.post("/factor-sets", { name: libName.trim(), locality: libLoc.trim(), items }); toast.success("Factor set saved"); setLibName(""); setLibLoc(""); loadSets(); }
    catch { toast.error("Save failed"); }
  };
  const loadSet = (id) => {
    const s = sets.find((x) => x.id === id); setSelKey((k) => k + 1); if (!s) return;
    setItems([...items, ...(s.items || [])]);
    toast.success(`Loaded ${(s.items || []).length} factor(s) from "${s.name}"`);
  };
  const deleteSet = async (id) => { try { await api.delete(`/factor-sets/${id}`); loadSets(); } catch { toast.error("Delete failed"); } };

  return (
    <div className="rounded-lg border p-3 space-y-3 bg-background" data-testid="neg-factors">
      <div>
        <Label className="text-sm font-semibold">Negative / disability factors</Label>
        <p className="text-[11px] text-muted-foreground mt-0.5">Deductions for site disabilities, applied to the base rate (the Step-3 adopted rate M if comparables exist, else the Guideline rate G). Net Adopted Rate = base × ΠFᵢ, Fᵢ = 1 − dᵢ. The total deduction is apportioned log-proportionally. {r.base <= 0 && <span className="text-amber-600">Set the Guideline rate G (or add comparables) above to compute.</span>}</p>
      </div>

      {/* Add formula factors */}
      <div className="flex flex-wrap items-center gap-2" data-testid="neg-library">
        <span className="text-[11px] font-medium">Factor library:</span>
        <Select key={selKey} onValueChange={loadSet}>
          <SelectTrigger className="h-8 w-56 text-xs" data-testid="neg-lib-load"><SelectValue placeholder="Load a saved factor set…" /></SelectTrigger>
          <SelectContent>
            {sets.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No saved sets yet</div>}
            {sets.map((s) => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}{s.locality ? ` · ${s.locality}` : ""} ({(s.items || []).length})</SelectItem>)}
          </SelectContent>
        </Select>
        <Button type="button" size="sm" variant="outline" className="rounded-full text-xs h-8" onClick={() => setLibOpen((v) => !v)} data-testid="neg-lib-toggle"><FloppyDisk size={13} className="mr-1" /> Save / manage</Button>
      </div>
      {libOpen && (
        <div className="rounded-lg border p-2.5 space-y-2 bg-muted/20" data-testid="neg-lib-panel">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1"><Label className="text-[11px]">Set name</Label><Input value={libName} onChange={(e) => setLibName(e.target.value)} placeholder="e.g. Industrial belt — Urla" className="h-8 w-52 text-xs" data-testid="neg-lib-name" /></div>
            <div className="space-y-1"><Label className="text-[11px]">Locality (optional)</Label><Input value={libLoc} onChange={(e) => setLibLoc(e.target.value)} placeholder="e.g. Raipur" className="h-8 w-40 text-xs" data-testid="neg-lib-locality" /></div>
            <Button type="button" size="sm" className="rounded-full text-xs h-8" onClick={saveSet} data-testid="neg-lib-save">Save current {items.length} factor(s)</Button>
          </div>
          {sets.length > 0 && (
            <div className="space-y-1">
              {sets.map((s) => (
                <div key={s.id} className="flex items-center gap-2 text-xs border-t pt-1" data-testid={`neg-lib-row-${s.id}`}>
                  <span className="flex-1">{s.name}{s.locality ? <span className="text-muted-foreground"> · {s.locality}</span> : null} <span className="text-muted-foreground">({(s.items || []).length})</span></span>
                  <Button type="button" size="sm" variant="ghost" className="text-[11px] h-6 px-2" onClick={() => loadSet(s.id)}>Load</Button>
                  <Button type="button" size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => deleteSet(s.id)} data-testid={`neg-lib-del-${s.id}`}><Trash size={12} /></Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add formula factors */}
      <div className="flex flex-wrap gap-1" data-testid="neg-formula-chips">
        <span className="text-[10px] text-muted-foreground self-center mr-0.5 flex items-center gap-0.5"><Plus size={10} /> Formula:</span>
        {Object.entries(NEG_DEFS).map(([type, def]) => (
          <Chip key={type} onClick={() => addFormula(type)} testid={`neg-add-${type}`}>{def.label}</Chip>
        ))}
      </div>

      {/* Add slab presets */}
      {NEG_SLABS.map((grp) => (
        <div key={grp.group} className="flex flex-wrap gap-1">
          <span className="text-[10px] text-muted-foreground self-center mr-0.5 w-36 shrink-0">{grp.group}:</span>
          {grp.items.map((s) => (
            <Chip key={s.label} onClick={() => addSlab(s)} title={`Indicative ${s.lo}–${s.hi}% (inserts ${Math.round((s.lo + s.hi) / 2)}%)`} testid={`neg-slab-${s.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              {s.label} <span className="text-muted-foreground">{s.lo}–{s.hi}%</span>
            </Chip>
          ))}
        </div>
      ))}

      {/* Factor cards */}
      <div className="space-y-2">
        {items.length === 0 && <p className="text-xs text-muted-foreground">No negative factors added.</p>}
        {items.map((it, i) => {
          const def = it.type === "custom" ? null : NEG_DEFS[it.type];
          const d = itemD(it, G);
          return (
            <div key={i} className="rounded-lg border p-2.5 space-y-2 bg-muted/20" data-testid={`neg-item-${i}`}>
              <div className="flex items-center gap-2">
                {it.type === "custom"
                  ? <Input value={it.label || ""} onChange={(e) => setItem(i, { label: e.target.value })} placeholder="Factor name" className="h-8 text-xs flex-1" data-testid={`neg-item-label-${i}`} />
                  : <span className="text-xs font-medium flex-1">{def ? def.label : it.type}</span>}
                <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeItem(i)} data-testid={`neg-item-del-${i}`}><Trash size={13} /></Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {it.type === "custom"
                  ? <div className="space-y-1"><Label className="text-[11px]">Deduction d (%)</Label><Input type="number" value={it.d ?? ""} onChange={(e) => setItem(i, { d: e.target.value })} className="h-8 w-28 text-xs" data-testid={`neg-item-d-${i}`} /></div>
                  : (def.params.map((pm) => (
                    <div key={pm.k} className="space-y-1"><Label className="text-[11px]">{pm.l}</Label>
                      <Input type="number" value={(it.p || {})[pm.k] ?? ""} onChange={(e) => setParam(i, pm.k, e.target.value)} className="h-8 w-32 text-xs" data-testid={`neg-param-${i}-${pm.k}`} /></div>
                  )))}
              </div>
              <div className="text-[11px] border-t pt-1.5">Deduction dᵢ = <b className="text-destructive">{pct(d)}</b> &nbsp;·&nbsp; Fᵢ = <b>{(1 - d).toFixed(3)}</b></div>
            </div>
          );
        })}
      </div>

      {/* Results */}
      {r.base > 0 && items.length > 0 && (
        <div className="rounded-lg border bg-muted/30 p-3 space-y-2" data-testid="neg-results">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
            <span>{r.fromComparables ? "Step-3 adopted rate M" : "Guideline rate G"} = <b data-testid="neg-base">{money(r.base)}/sq.ft</b></span>
            <span>Combined factor ΠF = <b className={r.crossCheck ? "text-destructive" : ""}>{r.piF.toFixed(3)}</b></span>
            <span>Net Adopted Rate = <b className="text-primary" data-testid="neg-M">{money(r.M)}/sq.ft</b></span>
            <span>Total deduction = <b className="text-destructive">{money(r.totalDed)}/sq.ft</b> ({pct(r.base ? r.totalDed / r.base : 0)})</span>
            {r.M > 0 && onAdopt && (
              <Button type="button" size="sm" variant="outline" className="rounded-full text-xs h-7 ml-auto" onClick={() => { onAdopt(r.M); toast.success(`Adopted Net rate ${money(r.M)}/sq.ft into Part A`); }} data-testid="neg-adopt">
                <ArrowBendDownRight size={13} className="mr-1" /> Use Net Adopted Rate as Part A rate
              </Button>
            )}
          </div>
          <table className="w-full text-xs mt-1">
            <thead><tr className="text-muted-foreground text-left"><th className="py-1">Factor</th><th>dᵢ</th><th>Fᵢ</th><th>Share</th><th className="text-right">₹/sq.ft</th></tr></thead>
            <tbody>
              {r.rows.map((x, i) => (
                <tr key={i} className="border-t"><td className="py-1">{x.label}</td><td className="text-destructive">−{pct(x.d)}</td><td>{x.F.toFixed(3)}</td><td>{pct(x.share)}</td><td className="text-right text-destructive">−{money(x.rupees)}</td></tr>
              ))}
              <tr className="border-t font-semibold"><td className="py-1">Total</td><td /><td>{r.piF.toFixed(3)}</td><td /><td className="text-right text-destructive">−{money(r.totalDed)}</td></tr>
            </tbody>
          </table>
          {r.crossCheck && <p className="text-[11px] text-amber-600 flex items-center gap-1"><WarningCircle size={12} /> ΠF &lt; 0.70 (deduction &gt;30%) — cross-check with the residual / development method.</p>}
          {r.reasoning && <p className="text-[11px] text-destructive flex items-center gap-1"><WarningCircle size={12} /> ΠF &lt; 0.50 (deduction &gt;50%) — record an explicit reasoning note.</p>}
          {r.belowSafe && <p className="text-[11px] text-amber-600">Tax note: rate is &gt;10% below guideline — outside the Sec. 50C / 56(2)(x) safe harbour.</p>}
        </div>
      )}

      {/* Residual / development-method cross-check */}
      {items.length > 0 && (
        <div className={"rounded-lg border p-3 space-y-2 " + (r.crossCheck ? "border-amber-400 bg-amber-50/60" : "bg-background")} data-testid="neg-residual">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Residual / development-method cross-check</Label>
            {r.crossCheck && <span className="text-[11px] text-amber-700 flex items-center gap-1" data-testid="neg-residual-flag"><WarningCircle size={12} /> ΠF &lt; 0.70 — cross-check required</span>}
          </div>
          <p className="text-[11px] text-muted-foreground">Independent land rate from development potential: GDV (built-up × sale rate) less construction, developer profit and soft costs, per sq.ft of land.</p>
          <div className="flex flex-wrap gap-2">
            <div className="space-y-1"><Label className="text-[11px]">FAR / FSI</Label><Input type="number" value={res.far ?? ""} onChange={(e) => setResidual({ far: e.target.value })} className="h-8 w-24 text-xs" data-testid="res-far" /></div>
            <div className="space-y-1"><Label className="text-[11px]">Efficiency %</Label><Input type="number" value={res.eff ?? ""} onChange={(e) => setResidual({ eff: e.target.value })} placeholder="80" className="h-8 w-24 text-xs" data-testid="res-eff" /></div>
            <div className="space-y-1"><Label className="text-[11px]">Sale rate ₹/sq.ft built</Label><Input type="number" value={res.saleRate ?? ""} onChange={(e) => setResidual({ saleRate: e.target.value })} className="h-8 w-32 text-xs" data-testid="res-sale" /></div>
            <div className="space-y-1"><Label className="text-[11px]">Construction ₹/sq.ft built</Label><Input type="number" value={res.constCost ?? ""} onChange={(e) => setResidual({ constCost: e.target.value })} className="h-8 w-32 text-xs" data-testid="res-const" /></div>
            <div className="space-y-1"><Label className="text-[11px]">Profit &amp; o/h %</Label><Input type="number" value={res.profitPct ?? ""} onChange={(e) => setResidual({ profitPct: e.target.value })} className="h-8 w-24 text-xs" data-testid="res-profit" /></div>
            <div className="space-y-1"><Label className="text-[11px]">Other / soft %</Label><Input type="number" value={res.otherPct ?? ""} onChange={(e) => setResidual({ otherPct: e.target.value })} className="h-8 w-24 text-xs" data-testid="res-other" /></div>
          </div>
          {rc.ready && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs border-t pt-2" data-testid="res-results">
              <span>GDV = <b>{money(rc.gdv)}/sq.ft land</b></span>
              <span>Residual land rate = <b className="text-primary" data-testid="res-rate">{money(rc.residualRate)}/sq.ft</b></span>
              <span>Net Adopted = <b>{money(r.M)}/sq.ft</b></span>
              <span className={rc.corroborated ? "text-green-700" : "text-amber-700"}>Divergence {(rc.divergence * 100).toFixed(1)}% — {rc.corroborated ? "corroborated" : "reconcile"}</span>
            </div>
          )}
        </div>
      )}

      {para && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Report paragraph</Label>
            <Button type="button" size="sm" variant="outline" className="rounded-full text-xs" onClick={() => { try { navigator.clipboard.writeText(para).then(() => toast.success("Copied")).catch(() => toast.error("Copy failed — select manually")); } catch { toast.error("Copy failed — select manually"); } }} data-testid="neg-copy"><Copy size={13} className="mr-1" /> Copy</Button>
          </div>
          <p className="text-xs leading-relaxed bg-background border rounded-md p-3" data-testid="neg-paragraph">{para}</p>
        </div>
      )}
    </div>
  );
}
